/**
 * GraphDefinition - abstract composition (blueprint)
 */

import { Endpoint } from '@/nodes/Endpoint';

/**
 * Raw config value (compact format)
 */
export type RawConfigValue = number | string | boolean | null | undefined | RawConfigValue[];

/**
 * Config value - either a raw value or a Partial<Endpoint> with metadata.
 * Compact format: 60, [1,2,3], "forward"
 * Full format: { value: 60, min: 0, max: 100 }
 *
 * At runtime, use isWrappedConfig() to check format and resolveConfigValue() to get value.
 */
export type ConfigValue = RawConfigValue | Partial<Endpoint>;

import type { Connection } from './Connection';


/**
 * Definition preset - named snapshot of exposed endpoint values.
 */
export interface DefinitionPreset {
  name: string;
  description?: string;
  params: Record<string, any>;  // exposedEndpointName -> value
}


/**
 * Exposed endpoint in a definition's API.
 * Extends Endpoint with path to internal endpoint.
 * dataType/dataFlow are optional - derived from internal endpoint at runtime.
 */
export interface ExposedEndpoint extends Omit<Endpoint, 'id' | 'dataType' | 'dataFlow'> {
  /** Path to internal endpoint: "nodeId.endpointId" */
  path: string;
  /** Optional override - usually derived from internal endpoint */
  dataType?: string;
  dataFlow?: string;
}

/**
 * Node config in a definition - stores type and optional metadata.
 * Future-proof structure for storing node-level state.
 */
export interface NodeConfig {
  type: string;
  label?: string;
}

/**
 * Abstract graph definition (blueprint).
 * Can contain primitive nodes or other graphs (recursive composition).
 * Defines internal wiring, exposed interface, and values.
 */
export interface GraphDefinition {
  id: string;

  /** Help text shown in info popup */
  info?: string;

  /** Visualization component name (e.g., 'orbitCanvas') */
  viz?: string;

  /** Internal node that handles visualization method calls (e.g., 'canvas') */
  vizNode?: string;

  // Sub-graph definitions (for self-contained projects)
  definitions?: Record<string, GraphDefinition>;

  // Nodes in this graph: nodeId -> config (type + optional metadata)
  // Also supports legacy format: nodeId -> type string (auto-converted on load)
  nodes: Record<string, NodeConfig | string>;

  // Wiring between nodes
  connections: Connection[];

  // api = definition's public interface: exposedName -> internalPath ("nodeId.endpointId")
  //  a set of exposed endpoints that map endpoint name to the internal endpoint's path
  /*
  Rules for default input / output settings derive from the internal endpoint config:
  - for Audio, Video, Event, Trigger endpoints:
    if the internal endpoint is input only, it should be input=true output=false on the api
    if it is output only it should be output=true, input=false on the exposed endpoint
    if it is both input and output, it should be output=true, input=false on the exposed endpoint. 
    if it is nether input nor output it should be input=fasle, output=false
 - for data endpoints (number, pattern, select etc)
   if the internal endpoint is input only, it should be input=false output=false on the api
   if it is output only, it should be input=false output=true on the api

  triggers and video, the same rules apply as for audio. for other endpoint types         
  (number, array, select) they should be output by default on the exposed endpoint if     
  they are output on the internal endpopint, but they should not be input by default. if  
  not output on the internal endpoint then the exposed endpoint should be neither input   
  nor output by default.  

  default values are also store here: exposedendpoint.value
  */
  api: Record<string, ExposedEndpoint>;

  // Internal endpoint values (shared by all instances, not exposed)
  // Nested format: { nodeId: { endpointId: value | Endpoint } }
  // Supports compact format (raw value) or full Endpoint with metadata
  config: Record<string, Record<string, ConfigValue>>;

  // Factory presets for this definition
  presets?: DefinitionPreset[];

  // User presets (loaded from files at runtime, not saved with definition)
  userPresets?: DefinitionPreset[];
}

/**
 * Get node type from NodeConfig or legacy string format
 */
export function getNodeType(nodeEntry: NodeConfig | string): string {
  if (!nodeEntry) {
    throw new Error('getNodeType called with undefined nodeEntry - node may have been removed');
  }
  return typeof nodeEntry === 'string' ? nodeEntry : nodeEntry.type;
}

/**
 * Get node label (returns undefined if not set)
 */
export function getNodeLabel(nodeEntry: NodeConfig | string): string | undefined {
  return typeof nodeEntry === 'string' ? undefined : nodeEntry.label;
}

/**
 * Normalize node entry to NodeConfig format
 */
export function toNodeConfig(nodeEntry: NodeConfig | string): NodeConfig {
  return typeof nodeEntry === 'string' ? { type: nodeEntry } : nodeEntry;
}
